// Lab Report
export const LAB_REPORT_NAME_MAP: Record<string, string> = {
  'labs--arterial-blood-gases': 'Arterial Blood Gases',
  'labs--blood-immunology': 'Blood Immunology',
  'labs--cardiac': 'Cardiac',
  'labs--cbc': 'CBC',
  'labs--chemistry': 'Chemistry',
  'labs--coagulation': 'Coagulation',
  'labs--electrolytes': 'Electrolytes',
  'labs--genetics': 'Genetics',
  'labs--lipids': 'Lipids',
  'labs--liver-function': 'Liver Function',
  'labs--microbiology': 'Microbiology',
  'labs--other': 'Other',
  'labs--other-urine-analyses': 'Other Urine Analyses',
  'labs--pathology': 'Pathology',
  'labs--thyroid': 'Thyroid',
  'labs--urine-analyses': 'Urine Analyses',
};

export const LAB_REPORT_TYPES = Object.entries(LAB_REPORT_NAME_MAP).map(
  ([key, value]) => ({ k: key, v: value })
);

export const LAB_TYPES: string[] = [
  'labs--arterial-blood-gases',
  'labs--blood-immunology',
  'labs--cardiac',
  'labs--cbc',
  'labs--chemistry',
  'labs--coagulation',
  'labs--electrolytes',
  'labs--genetics',
  'labs--lipids',
  'labs--liver-function',
  'labs--microbiology',
  'labs--other',
  'labs--other-urine-analyses',
  'labs--pathology',
  'labs--thyroid',
  'labs--urine-analyses',
];

// Observation: Labs Arterial Blood Gases
export const LAB_ARTERIAL_OBSERVATION_CODE_MAP: Record<string, string> = {
  ph: 'pH',
  'partial-pressure-of-carbon-dioxide--pc02':
    'Partial Pressure of Carbon Dioxide (PC02)',
  bicarbonate: 'Bicarbonate',
  'partial-pressure-of-oxygen--p02': 'Partial Pressure of Oxygen (P02)',
  'oxygen-saturation': 'Oxygen Saturation',
  'arterial-oxygen-content': 'Arterial Oxygen Content',
  'venous-oxygen-content': 'Venous Oxygen Content',
  'base-excess-deficit': 'Base Excess/Deficit',
  'alveolar-to-arterial-oxygen-difference':
    'Alveolar-to-Arterial Oxygen Difference',
  'serum-lactate': 'Serum Lactate',
};

export const LAB_ARTERIAL_UNIT_MAP: Record<string, string[]> = {
  'partial-pressure-of-carbon-dioxide--pc02': ['kPa', 'mmHg'],
  bicarbonate: ['mmol/L', 'mEq/L'],
  'partial-pressure-of-oxygen--p02': ['kPa', 'mmHg'],
  'oxygen-saturation': ['%'],
  'arterial-oxygen-content': ['%'],
  'venous-oxygen-content': ['%'],
  'base-excess-deficit': ['mmol/L', 'mEq/L'],
  'alveolar-to-arterial-oxygen-difference': ['mmHg'],
  'serum-lactate': ['mmol/L', 'nmol/L'],
  other: [],
};

// Observation: Labs Blood Immunology
export const LAB_IMMUNOLOGY_OBSERVATION_CODE_MAP: Record<string, string> = {
  'anti-a-anti-b-titre': 'Anti-A/Anti-B Titre',
  'anti-a-titre': 'Anti-A Titre',
  'anti-b-titre': 'Anti-B Titre',
  'anti-iga-antibody': 'Anti-IgA Antibody',
  'antibody-investigation': 'Antibody Investigation',
  'blood-group---antibody-screen': 'Blood Group & Antibody Screen',
  'blood-group-confirmation': 'Blood Group Confirmation',
  'rh-factor-test': 'RH Factor Test',
  'cold-agglutinin-screen': 'Cold Agglutinin Screen',
  crossmatch: 'Crossmatch',
  'smooth-muscle-antibodies': 'Smooth Muscle Antibodies (SMA)',
  'hla-b-27-antigen': 'HLA B 27 Antigen',
  'angiotension-converting-enzyme': 'Angiotensin Converting Enzyme (ACE)',
  'anti-myeloperoxidase': 'Anti myeloperoxidase (Anti-MPO)',
  'microsomal-ab-microsomal-tpo': 'Microsomal AB/Microsomal TPO',
  'proteinase-3': 'Proteinase 3 (Anti-PR3)',
  iga: 'IgA',
  igg: 'IgG',
  igm: 'IgM',
  serum_neutralizing_antibody__SNA: 'Serum Neutralizing Antibody (SNA)',
  antinuclear_antibody__ANA: 'Antinuclear Antibody (ANA)',
  antimitochondrial_antibody__AMA: 'Antimitochondrial Antibody (AMA)',
  'free-light-chains-kappa': 'Free Light Chains Kappa',
  'free-light-chains-lambda': 'Free Light Chains Lambda',
  'kappa-lambda-ratio': 'Kappa/Lambda Ratio',
  'tissue-transglutaminase-iga-ab': 'Tissue transglutaminase IgA Ab',
  other: 'Other',
};

export const LAB_IMMUNOLOGY_UNIT_MAP: Record<string, string[]> = {
  'anti-a-anti-b-titre': ['Positive', 'Negative'],
  'anti-a-titre': ['Positive', 'Negative'],
  'anti-b-titre': ['Positive', 'Negative'],
  'anti-iga-antibody': [],
  'antibody-investigation': [],
  'blood-group---antibody-screen': [],
  'blood-group-confirmation': [],
  'rh-factor-test': ['Positive', 'Negative'],
  'cold-agglutinin-screen': [],
  crossmatch: [],
  'smooth-muscle-antibodies': ['Positive', 'Negative'],
  'hla-b-27-antigen': [],
  'angiotension-converting-enzyme': ['U/L'],
  'anti-myeloperoxidase': [],
  'microsomal-ab-microsomal-tpo': ['Positive', 'Negative'],
  'proteinase-3': [],
  iga: ['g/L'],
  igg: ['g/L'],
  igm: ['g/L'],
  serum_neutralizing_antibody__SNA: ['Positive', 'Negative'],
  antinuclear_antibody__ANA: ['Positive', 'Negative'],
  antimitochondrial_antibody__AMA: ['Positive', 'Negative'],
  'free-light-chains-kappa': ['mg/L'],
  'free-light-chains-lambda': ['mg/L'],
  'kappa-lambda-ratio': [],
  'tissue-transglutaminase-iga-ab': ['U/mL'],
  other: [],
};

// Observation: Labs Cardiac
export const LAB_CARDIAC_OBSERVATION_CODE_MAP: Record<string, string> = {
  'creatine-kinase': 'Creatine Kinase',
  'troponin-t': 'Troponin T',
  'troponin-i': 'Troponin I',
  'myoglobin--blood': 'Myoglobin, blood',
  'atrial-natriuretic-peptide--anp': 'Atrial Natriuretic Peptide (ANP)',
  'b-type-natriuretic-peptide--bnp': 'B-Type Natriuretic Peptide (BNP)',
  digoxin: 'Digoxin',
  'ischemia-modified-albumin--ima': 'Ischemia-Modified Albumin (IMA)',
  'nt-pro-b-type-natriuretic-peptide':
    'N-Terminal Pro-B-Type Natriuretic Peptide (NT-proBNP)',
  other: 'Other',
};

export const LAB_CARDIAC_UNIT_MAP: Record<string, string[]> = {
  'creatine-kinase': ['U/L', 'nmol sec/L'],
  'troponin-t': ['mcg/L', 'ng/mL'],
  'troponin-i': ['mcg/L', 'ng/mL'],
  'myoglobin--blood': ['nmol/L', 'ng/mL'],
  'atrial-natriuretic-peptide--anp': ['mcg/L', 'ng/mL'],
  'b-type-natriuretic-peptide--bnp': ['mcg/L', 'ng/mL'],
  digoxin: ['nmol/L', 'ng/mL'],
  'ischemia-modified-albumin--ima': ['IU/L'],
  'nt-pro-b-type-natriuretic-peptide': ['pg/mL'],
  other: [],
};

// Observation: Labs CBC
export const LAB_CBC_OBSERVATION_CODE_MAP: Record<string, string> = {
  hemoglobin: 'Hemoglobin',
  wbc: 'WBC',
  platelets: 'Platelets',
  neutrophils: 'Neutrophils',
  lymphocytes: 'Lymphocytes',
  monocytes: 'Monocytes',
  eosinophils: 'Eosinophils',
  basophils: 'Basophils',
  rbc: 'RBC',
  hematocrit: 'Hematocrit',
  'mean-cell-volume--mcv': 'Mean Cell Volume (MCV)',
  'mean-cell-hemoglobin--mch': 'Mean Cell Hemoglobin (MCH)',
  'mean-corpuscular-hemoglobin-concentration':
    'Mean Corpuscular Hemoglobin Concentration (MCHC)',
  reticulocyte: 'Reticulocyte',
  morphologyplatelets: 'RBC Morphology Platelets',
  'red-cell-distribution--rdw': 'Red Cell Distribution (RDW)',
  'erythroctye-sedimentation-rate': 'Erythroctye Sedimentation Rate',
  ovalocytes: 'Ovalocytes',
  plasminogen: 'Plasminogen',
  'platelet-closure-time': 'Platelet Closure Time',
  immaturegranulocytes: 'Immature Granulocytes (absolute)',
  'mean-platelet-volume--mpv': 'Mean Platelet Volume (MPV)',
  'nucleated-rbc': "Nucleated RBC's",
  'nucleated-rbc-percentage-of-wbc': "Nucleated RBC's as % of WBC's",
  other: 'Other',
};

export const LAB_CBC_UNIT_MAP: Record<string, string[]> = {
  hemoglobin: ['mmol/L', 'g/L'],
  wbc: ['10^9/L', '/mm^3'],
  platelets: ['10^9/L', '/mm^3'],
  neutrophils: ['10^9/L', '/mm^3'],
  lymphocytes: ['10^9/L', '/mm^3'],
  monocytes: ['10^9/L', '/mm^3'],
  eosinophils: ['10^9/L', '/mm^3'],
  basophils: ['10^9/L', '/mm^3'],
  rbc: ['10^12/L'],
  hematocrit: ['No Unit', '%'],
  'mean-cell-volume--mcv': ['fL', '/mm^3'],
  'mean-cell-hemoglobin--mch': ['pg'],
  'mean-corpuscular-hemoglobin-concentration': ['%', 'g/L'],
  reticulocyte: ['%'],
  morphologyplatelets: [],
  'red-cell-distribution--rdw': ['%'],
  'erythroctye-sedimentation-rate': ['mm/hr'],
  ovalocytes: [],
  plasminogen: ['mg/dL', 'IU/mL'],
  'platelet-closure-time': ['Seconds'],
  immaturegranulocytes: ['10^9/L', '/mm^3'],
  'mean-platelet-volume--mpv': ['fL'],
  'nucleated-rbc': ['/100 WBC'],
  'nucleated-rbc-percentage-of-wbc': [],
  other: [],
};

// Observation: Labs Chemistry
export const LAB_CHEMISTRY_OBSERVATION_CODE_MAP: Record<string, string> = {
  'serum-iron': 'Serum iron',
  'total-iron-binding-capacity--tibc': 'Total Iron Binding Capacity (TIBC)',
  'transferrin-assay': 'Transferrin Assay',
  'transferrin-saturation': 'Transferrin saturation',
  ferritin: 'Ferritin',
  protein: 'Protein',
  'a/g-ratio': 'A/G Ratio',
  'c-reactive-protein': 'C-Reactive Protein',
  'erythropoietin--epo': 'Erythropoietin (EPO)',
  prealbumin: 'Prealbumin',
  albumin: 'Albumin',
  'antidiuretic-hormone--adh': 'Antidiuretic Hormone (ADH)',
  'zinc--plasma': 'Zinc, Plasma',
  pregnanediol: 'Pregnanediol',
  'total-estrogen--blood': 'Total Estrogen, Blood',
  'phenytoin--free': 'Phenytoin, Free',
  'prostate-specfic-antigen--psa': 'Prostate Specfic Antigen (PSA)',
  'hla-b27': 'HLA-B27',
  'alpha-1-antitrypsin': 'Alpha-1 antitrypsin (AAT)',
  'alpha-1-globulin': 'Alpha-1 Globulin',
  'alpha-2-globulin': 'Alpha-2 Globulin',
  'beta-1-globulin': 'Beta 1 Globulin',
  'beta-2-globulin': 'Beta 2 Globulin',
  'gamma-globulin': 'Gamma Globulin',
  ceruloplasmin: 'Ceruloplasmin',
  'iron-saturation': 'Iron Saturation',
  'unsaturated-iron-binding-capacity':
    'Unsaturated iron binding capacity (UIBC)',
  'anion-gap': 'Anion Gap',
  'lactate-dehydrogenase': 'Lactate dehydrogenase (LDH)',
  lipase: 'Lipase',
  'adrenocorticotropic-hormone-plasma-acth':
    'Adrenocorticotropic hormone, plasma (ACTH)',
  cortisol: 'Cortisol',
  ammonia: 'Ammonia',
  homocysteine: 'Homocysteine',
  lead: 'Lead',
  mercury: 'Mercury',
  amylase_blood: 'Amylase, Blood',
  'free-psa': 'Free PSA',
  'free-total-psa-ratio': 'Free/Total PSA ratio',
  other: 'Other',
};

export const LAB_CHEMISTRY_UNIT_MAP: Record<string, string[]> = {
  'serum-iron': ['mcmol/L', 'mcg/dL'],
  'total-iron-binding-capacity--tibc': ['mcmol/L', 'mcg/dL'],
  'transferrin-assay': ['g/L', 'mg/dL'],
  'transferrin-saturation': ['%'],
  ferritin: ['mcg/L', 'ng/mL'],
  protein: ['g/L'],
  'a/g-ratio': [],
  'c-reactive-protein': ['mg/L', 'mg/dL'],
  'erythropoietin--epo': ['IU/L'],
  prealbumin: ['g/L', 'mg/dL'],
  albumin: ['g/L', 'mg/dL'],
  'antidiuretic-hormone--adh': ['pmol/L', 'pg/mL'],
  'zinc--plasma': ['umol/L', 'ug/dL'],
  pregnanediol: ['mcmol/day', 'mg/day'],
  'total-estrogen--blood': ['ng/L'],
  'phenytoin--free': ['umol/L', 'ug/mL'],
  'prostate-specfic-antigen--psa': ['mcg/L', 'ng/L'],
  'hla-b27': ['Positive', 'Negative'],
  'alpha-1-antitrypsin': ['g/L', 'mg/dL'],
  'alpha-1-globulin': ['g/L', 'g/dL', '%'],
  'alpha-2-globulin': ['g/L', 'g/dL', '%'],
  'beta-1-globulin': ['g/L', 'g/dL'],
  'beta-2-globulin': ['g/L', 'g/dL'],
  'gamma-globulin': ['g/L', 'g/dL'],
  ceruloplasmin: ['mg/L', 'mg/dL'],
  'iron-saturation': [],
  'unsaturated-iron-binding-capacity': ['mcmol/L', 'ug/dL'],
  'anion-gap': ['No unit'],
  'lactate-dehydrogenase': ['U/L', 'IU/L'],
  lipase: ['U/L'],
  'adrenocorticotropic-hormone-plasma-acth': ['pmol/L'],
  cortisol: ['mmol/L', 'mcg/dL'],
  ammonia: ['mmol/L', 'mcg/dL'],
  homocysteine: ['umol/L'],
  lead: ['umol/L'],
  mercury: ['nmol/L'],
  amylase_blood: ['U/L'],
  'free-psa': ['μg/L'],
  'free-total-psa-ratio': [],
  other: [],
};

// Observation: Labs Coagulation
export const LAB_COAGULATION_OBSERVATION_CODE_MAP: Record<string, string> = {
  'prothrombin-time--pt': 'Prothrombin Time (PT)',
  inr: 'INR',
  'activated-partial-thromboplastin-time--appt':
    'Activated Partial Thromboplastin Time (aPTT)',
  'partial-thromboplastin-time--ptt': 'Partial Thromboplastin Time (PTT)',
  'd-dimer': 'D-dimer',
  'fibrinogen--factor-1': 'Fibrinogen (Factor 1)',
  'anti-factor-xa-assay--unfractionated':
    'Anti–Factor Xa Assay (unfractionated)',
  'factor-ii': 'Factor II',
  'factor-v': 'Factor V',
  'factor-vii': 'Factor VII',
  'factor-viii': 'Factor VIII',
  'factor-ix': 'Factor IX',
  'factor-x': 'Factor X',
  'factor-xi': 'Factor XI',
  'factor-xii': 'Factor XII',
  'von-willebrand-factor-antigen': 'Von Willebrand Factor Antigen',
  'anticardiolipin-antibody-igg-gpl': 'Anticardiolipin Antibody IgG (GPL)',
  'anticardiolipin-antibody-igm-mpl': 'Anticardiolipin Antibody IgM (MPL)',
  other: 'Other',
};

export const LAB_COAGULATION_UNIT_MAP: Record<string, string[]> = {
  'prothrombin-time--pt': ['seconds'],
  inr: [],
  'activated-partial-thromboplastin-time--appt': ['seconds'],
  'partial-thromboplastin-time--ptt': ['seconds'],
  'd-dimer': ['mg/LFEU', 'ng/mL'],
  'fibrinogen--factor-1': ['mcmol/L', 'mg/dL'],
  'anti-factor-xa-assay--unfractionated': ['IU/mL'],
  'factor-ii': ['IU/mL', '%'],
  'factor-v': ['IU/mL', '%'],
  'factor-vii': ['IU/mL', '%'],
  'factor-viii': ['IU/mL', '%'],
  'factor-ix': ['IU/mL', '%'],
  'factor-x': ['IU/mL', '%'],
  'factor-xi': ['IU/mL', '%'],
  'factor-xii': ['IU/mL', '%'],
  'von-willebrand-factor-antigen': ['IU/mL', '%'],
  'anticardiolipin-antibody-igg-gpl': ['GPL'],
  'anticardiolipin-antibody-igm-mpl': ['MPL'],
  other: [],
};

// Observation: Labs Electrolytes
export const LAB_ELECTROLYTES_OBSERVATION_CODE_MAP: Record<string, string> = {
  sodium: 'Sodium',
  potassium: 'Potassium',
  chloride: 'Chloride',
  phosphate: 'Phosphate',
  magnesium: 'Magnesium',
  calcium: 'Calcium',
  'calcium-ionized': 'Calcium ionized',
  'creatinine--blood': 'Creatinine, Blood',
  'creatinine-clearance': 'Creatinine Clearance',
  bun: 'BUN',
  hgba1c: 'HgbA1C',
  'glucose-fasting': 'Glucose, Fasting',
  'glucose--blood': 'Glucose, Random',
  'glucose--csf': 'Glucose, CSF',
  'folate--folic-acid': 'Folate, Folic Acid',
  'lactic-acid--venous': 'Lactic acid, Venous',
  'lactic-acid--arterial': 'Lactic acid, Arterial',
  'carbon-dioxide': 'Carbon dioxide',
  'gfr-glomerular-filtration-rate': 'GFR (Glomerular Filtration Rate)',
  anion_gap: 'Anion Gap',
  amylase_blood: 'Amylase, Blood',
  'bicarbonate--hco3': 'Bicarbonate (HCO₃⁻)',
  'carbon-dioxide--co2': 'Carbon dioxide (CO₂)',
  'corrected-calcium': 'Corrected Calcium',
  lithium: 'Lithium',
  other: 'Other',
};

export const LAB_ELECTROLYTES_UNIT_MAP: Record<string, string[]> = {
  sodium: ['mmol/L', 'mEq/L'],
  potassium: ['mmol/L', 'mEq/L'],
  chloride: ['mmol/L', 'mEq/L'],
  phosphate: ['mmol/L', 'mEq/L'],
  magnesium: ['mmol/L', 'mEq/L'],
  calcium: ['mmol/L', 'mEq/L'],
  'calcium-ionized': ['mmol/L', 'mg/dL'],
  'creatinine--blood': ['mcmol/L', 'mg/dL'],
  'creatinine-clearance': ['mL/sec', 'mL/min'],
  bun: ['mmol/L', 'mg/dL'],
  hgba1c: ['%'],
  'glucose-fasting': ['mmol/L', 'mg/dL'],
  'glucose--blood': ['mmol/L', 'mg/dL'],
  'glucose--csf': ['mmol/L', 'mg/dL'],
  'folate--folic-acid': ['nmol/L', 'ng/mL'],
  'lactic-acid--venous': ['mmol/L', 'mg/dL'],
  'lactic-acid--arterial': ['mmol/L', 'mg/dL'],
  'carbon-dioxide': ['mmol/L', 'mEq/L'],
  'gfr-glomerular-filtration-rate': ['mL/min/1.73 m2'],
  anion_gap: ['mmol/L', 'mEq/L'],
  amylase_blood: ['U/L'],
  'bicarbonate--hco3': ['mEq/L', 'mmol/L'],
  'carbon-dioxide--co2': ['mEq/L', 'mmol/L'],
  'corrected-calcium': ['mmol/L'],
  lithium: ['mmol/L'],
  other: [],
};

// Observation: Labs Genetics
export const LAB_GENETICS_OBSERVATION_CODE_MAP: Record<string, string> = {
  'birt-hogg-dube': 'Birt-Hogg Dube',
  'familial-melanoma': 'Familial Melanoma',
  'leiomyomatosis-and-renal-cell-carcinoma':
    'Leiomyomatosis and Renal Cell Carcinoma',
  'thrombosis--genome-diagnostics': 'Thrombosis (Genome Diagnostics)',
  'cowden-syndrome': 'Cowden Syndrome',
  haemochromatosis: 'Haemochromatosis',
  brac1: 'BRAC1',
  other: 'Other',
};

export const LAB_GENETICS_UNIT_MAP: Record<string, string[]> = {
  'birt-hogg-dube': ['Positive', 'Negative'],
  'familial-melanoma': ['Positive', 'Negative'],
  'leiomyomatosis-and-renal-cell-carcinoma': ['Positive', 'Negative'],
  'thrombosis--genome-diagnostics': ['Positive', 'Negative'],
  'cowden-syndrome': ['Positive', 'Negative'],
  haemochromatosis: ['Positive', 'Negative'],
  brac1: ['Positive', 'Negative'],
  other: [],
};

// Observation: Labs Lipids
export const LAB_LIPID_OBSERVATION_CODE_MAP: Record<string, string> = {
  totalcholesterol: 'Total Cholesterol',
  hdl: 'HDL',
  ldl: 'LDL',
  vldl: 'VLDL',
  triglycerides: 'Triglycerides',
  'bile-acids': 'Bile Acids',
  'non-hdl-cholesterol': 'non-HDL Cholesterol',
  'ldl-hdl-ratio': 'LDL to HDL Ratio',
  apoB: 'Apolipoprotein B (Apo-B)',
  lipase: 'Lipase',
  'tc-hdl': 'TC-HDL',
  'hours-fasting': 'Hour Fasting',
  'lipoprotein-a': 'Lipoprotein A',
  'chol-hdl': 'CHOL/HDL',
  'hours-after-meal': 'Hours after a meal',
  other: 'Other',
};

export const LAB_LIPID_UNIT_MAP: Record<string, string[]> = {
  totalcholesterol: ['mmol/L', 'mg/dL'],
  hdl: ['mmol/L', 'mg/dL'],
  ldl: ['mmol/L', 'mg/dL'],
  vldl: ['mmol/L', 'mg/dL'],
  triglycerides: ['mmol/L', 'mg/dL'],
  'bile-acids': ['mmol/L'],
  'non-hdl-cholesterol': ['mmol/L', 'mg/dL'],
  'ldl-hdl-ratio': [],
  apoB: ['g/L', 'mg/dL'],
  lipase: ['U/L'],
  'tc-hdl': [],
  'hours-fasting': ['hours'],
  'lipoprotein-a': ['nmol/L'],
  'chol-hdl': [],
  'hours-after-meal': ['hours'],
  other: [],
};

// Observation: Labs Liver Function
export const LAB_LIVER_OBSERVATION_CODE_MAP: Record<string, string> = {
  alt: 'ALT',
  ast: 'AST',
  alp: 'ALP',
  'total-bilirubin': 'Total Bilirubin',
  'bilirubin-conj--direct': 'Bilirubin Conj (Direct)',
  'indirect-bilirubin': 'Indirect Bilirubin',
  'alpha-1-antitrypsin': 'Alpha-1 antitrypsin (AAT)',
  other: 'Other',
};

export const LAB_LIVER_UNIT_MAP: Record<string, string[]> = {
  alt: ['U/L'],
  ast: ['U/L'],
  alp: ['IU/L', 'ukat/L'],
  'total-bilirubin': ['mcmol/L', 'mg/dL'],
  'bilirubin-conj--direct': ['mcmol/L', 'mg/dL'],
  'indirect-bilirubin': ['mcmol/L'],
  'alpha-1-antitrypsin': ['g/L', 'mg/dL'],
  other: [],
};

// Observation: Labs Microbiology
export const LAB_MICROBIOLOGY_OBSERVATION_CODE_MAP: Record<string, string> = {
  'beta-hemolytic-streptococcus-group-a':
    'Beta Hemolytic Streptococcus Group A',
  'campylobacter-serology': 'Campylobacter Serology',
  'carbapenemase-screen': 'Carbapenemase Screen (CRE)',
  'chlamydia-culture': 'Chlamydia Culture',
  'cryptococcus-serology': 'Cryptococcus Serology',
  'c---s--abscess': 'C & S- Abscess',
  'c---s-blood': 'C & S-Blood',
  'c---s--bone': 'C & S- Bone',
  'c---s--bone-marrow': 'C & S-Bone Marrow',
  'c---s-corneal-scraping': 'C & S-Corneal Scraping',
  'c---s--csf': 'C & S-CSF',
  'c---s--csf-drainage': 'C & S- CSF Drainage',
  'c---s--dialysis': 'C & S- Dialysis',
  'c---s--dialysis-fluid--peritoneal': 'C & S- Dialysis Fluid, Peritoneal',
  'c---s--eye': 'C & S-Eye',
  'c---s--needle-aspirates': 'C & S-Needle Aspirates',
  'c---s--neonatal-blood': 'C & S- Neonatal Blood',
  'c---s--respiratory-fluids': 'C & S- Respiratory Fluids',
  'c---s--respiratory-tissues': 'C & S- Respiratory Tissues',
  'c---s--urine': 'C & S- Urine',
  'c---s--wounds': 'C & S- Wounds',
  'c---s--stool': 'C & S- Stool',
  'c--difficile-toxin-a---b': 'C. Difficile Toxin A & B',
  'cytomegalovirus-igg': 'Cytomegalovirus IgG',
  'cytomegalovirus-igm': 'Cytomegalovirus IgM',
  'dengue-virus': 'Dengue Virus',
  'dermatophyte-test-medium': 'Dermatophyte Test Medium',
  'epstein-barr-virus': 'Epstein-Barr Virus',
  'epstein-barr-virus-igm': 'Epstein-Barr Virus IgM',
  'fungus-abscess': 'Fungus-Abscess',
  'fungus--blood': 'Fungus-Blood',
  'fungus--bone': 'Fungus- Bone',
  'fungus-csf': 'Fungus-CSF',
  'fungus--csf-drainage': 'Fungus-CSF Drainage',
  'fungus--eye': 'Fungus- Eye',
  'fungus-joint': 'Fungus-Joint',
  'fungus-mouth-swabs': 'Fungus-Mouth Swabs',
  'fungus-need-aspirate': 'Fungus-Needle Aspirate',
  'fungus-respiratory-fluids': 'Fungus-Respiratory Fluids',
  'fungus-respiratory-tissues': 'Fungus-Respiratory Tissues',
  'fungus-skin-swab': 'Fungus-Skin Swab',
  'fungus-urine': 'Fungus-Urine',
  'fungus-wound': 'Fungus-Wound',
  'group-b-streptococcus': 'Group B Streptococcus',
  gonorrhea: 'Gonorrhea',
  'helicobacter-pylori': 'Helicobacter pylori',
  'hepatitis-a-igg': 'Hepatitis A IgG',
  'hepatitis-a-igm': 'Hepatitis A IgM',
  'hepatitis-b-surface-antigen': 'Hepatitis B Surface Antigen',
  'hepatitis-b-surface-antibody': 'Hepatitis B Surface Antibody',
  'hepatitis-be-antibody': 'Hepatitis Be Antibody',
  'hepatitis-be-antigen': 'Hepatitis Be Antigen',
  'hepatitis-c-antibody': 'Hepatitis C Antibody',
  'hepatitis-c-rna--quantitative-viral-load':
    'Hepatitis C RNA- Quantitative Viral Load',
  'hepatitis-core-igm': 'Hepatitis Core IgM',
  'hepatitis-core-total-antibodies': 'Hepatitis Core Total Antibodies',
  'hiv-antibody': 'HIV Antibody',
  'hiv-viral-load': 'HIV Viral Load',
  hpv: 'HPV',
  'legionella-culture': 'Legionella Culture',
  'occult-blood': 'Occult blood',
  'wbc-stool': 'WBC Stool',
  'influenza-a---b': 'Influenza A & B',
  malaria: 'Malaria',
  'measles-igg': 'Measles IgG',
  monospot: 'Monospot',
  'mumps-igm': 'Mumps IgM',
  'mumps-igg-total': 'Mumps IgG/Total',
  mrsa: 'MRSA',
  pinworm: 'Pinworm',
  rabies: 'Rabies',
  rickettsia: 'Rickettsia',
  'rubella-igg': 'Rubella IgG',
  'rubella-igm': 'Rubella IgM',
  'rapid-plasma-reagin': 'Rapid Plasma Reagin',
  'sars-cov2': 'SARS-CoV2',
  'toxoplasma-igg': 'Toxoplasma IgG',
  'toxoplasma-igm': 'Toxoplasma IgM',
  'vaginal-swab': 'Vaginal Swab',
  'varicella-zoster-igg': 'Varicella Zoster IgG',
  'varicella-zoster-igm': 'Varicella Zoster IgM',
  vre: 'VRE',
  'zika-virus-pcr-amniotic-fluid': 'Zika Virus PCR-Amniotic Fluid',
  'zika-virus-pcr-csf': 'Zika Virus PCR-CSF',
  'zika-virus-pcr--serum': 'Zika Virus PCR- Serum',
  'zika-virus-pcr-tissue': 'Zika Virus PCR-Tissue',
  'zika-virus-pcr-urine': 'Zika Virus PCR-Urine',
  'aerobic-culture-genital-vaginal': 'Aerobic culture - genital/vaginal',
  trichomonas: 'Trichomonas',
  'fecal-immunochemical-test': 'Fecal Immunochemical Test (FIT)',
  other: 'Other',
};

export const LAB_MICROBIOLOGY_UNIT_MAP: Record<string, string[]> = {
  'beta-hemolytic-streptococcus-group-a': ['Positive', 'Negative'],
  'campylobacter-serology': ['Positive', 'Negative'],
  'carbapenemase-screen': ['Positive', 'Negative'],
  'chlamydia-culture': ['Positive', 'Negative'],
  'cryptococcus-serology': ['Positive', 'Negative'],
  'c---s--abscess': ['Positive', 'Negative'],
  'c---s-blood': ['Positive', 'Negative'],
  'c---s--bone': ['Positive', 'Negative'],
  'c---s--bone-marrow': ['Positive', 'Negative'],
  'c---s-corneal-scraping': ['Positive', 'Negative'],
  'c---s--csf': ['Positive', 'Negative'],
  'c---s--csf-drainage': ['Positive', 'Negative'],
  'c---s--dialysis': ['Positive', 'Negative'],
  'c---s--dialysis-fluid--peritoneal': ['Positive', 'Negative'],
  'c---s--eye': ['Positive', 'Negative'],
  'c---s--needle-aspirates': ['Positive', 'Negative'],
  'c---s--neonatal-blood': ['Positive', 'Negative'],
  'c---s--respiratory-fluids': ['Positive', 'Negative'],
  'c---s--respiratory-tissues': ['Positive', 'Negative'],
  'c---s--urine': ['Positive', 'Negative'],
  'c---s--wounds': ['Positive', 'Negative'],
  'c---s--stool': ['Positive', 'Negative'],
  'c--difficile-toxin-a---b': ['Positive', 'Negative'],
  'cytomegalovirus-igg': ['Positive', 'Negative'],
  'cytomegalovirus-igm': ['Positive', 'Negative'],
  'dengue-virus': ['Positive', 'Negative'],
  'dermatophyte-test-medium': ['Positive', 'Negative'],
  'epstein-barr-virus': ['Positive', 'Negative'],
  'epstein-barr-virus-igm': ['Positive', 'Negative'],
  'fungus-abscess': ['Positive', 'Negative'],
  'fungus--blood': ['Positive', 'Negative'],
  'fungus--bone': ['Positive', 'Negative'],
  'fungus-csf': ['Positive', 'Negative'],
  'fungus--csf-drainage': ['Positive', 'Negative'],
  'fungus--eye': ['Positive', 'Negative'],
  'fungus-joint': ['Positive', 'Negative'],
  'fungus-mouth-swabs': ['Positive', 'Negative'],
  'fungus-need-aspirate': ['Positive', 'Negative'],
  'fungus-respiratory-fluids': ['Positive', 'Negative'],
  'fungus-respiratory-tissues': ['Positive', 'Negative'],
  'fungus-skin-swab': ['Positive', 'Negative'],
  'fungus-urine': ['Positive', 'Negative'],
  'fungus-wound': ['Positive', 'Negative'],
  'group-b-streptococcus': ['Positive', 'Negative'],
  gonorrhea: ['Positive', 'Negative'],
  'helicobacter-pylori': ['Positive', 'Negative'],
  'hepatitis-a-igg': ['Positive', 'Negative'],
  'hepatitis-a-igm': ['Positive', 'Negative'],
  'hepatitis-b-surface-antigen': ['Positive', 'Negative'],
  'hepatitis-b-surface-antibody': ['Positive', 'Negative'],
  'hepatitis-be-antibody': ['Positive', 'Negative'],
  'hepatitis-be-antigen': ['Positive', 'Negative'],
  'hepatitis-c-antibody': ['Positive', 'Negative'],
  'hepatitis-c-rna--quantitative-viral-load': ['Positive', 'Negative'],
  'hepatitis-core-igm': ['Positive', 'Negative'],
  'hepatitis-core-total-antibodies': ['Positive', 'Negative'],
  'hiv-antibody': ['Positive', 'Negative'],
  'hiv-viral-load': ['Positive', 'Negative'],
  hpv: ['Positive', 'Negative'],
  'legionella-culture': ['Positive', 'Negative'],
  'occult-blood': ['Positive', 'Negative'],
  'wbc-stool': ['Positive', 'Negative'],
  'influenza-a---b': ['Positive', 'Negative'],
  malaria: ['Positive', 'Negative'],
  'measles-igg': ['Positive', 'Negative'],
  monospot: ['Positive', 'Negative'],
  'mumps-igm': ['Positive', 'Negative'],
  'mumps-igg-total': ['Positive', 'Negative'],
  mrsa: ['Positive', 'Negative'],
  pinworm: ['Positive', 'Negative'],
  rabies: ['Positive', 'Negative'],
  rickettsia: ['Positive', 'Negative'],
  'rubella-igg': ['Positive', 'Negative'],
  'rubella-igm': ['Positive', 'Negative'],
  'rapid-plasma-reagin': ['Positive', 'Negative'],
  'sars-cov2': ['Positive', 'Negative'],
  'toxoplasma-igg': ['Positive', 'Negative'],
  'toxoplasma-igm': ['Positive', 'Negative'],
  'vaginal-swab': ['Positive', 'Negative'],
  'varicella-zoster-igg': ['Positive', 'Negative'],
  'varicella-zoster-igm': ['Positive', 'Negative'],
  vre: ['Positive', 'Negative'],
  'zika-virus-pcr-amniotic-fluid': ['Positive', 'Negative'],
  'zika-virus-pcr-csf': ['Positive', 'Negative'],
  'zika-virus-pcr--serum': ['Positive', 'Negative'],
  'zika-virus-pcr-tissue': ['Positive', 'Negative'],
  'zika-virus-pcr-urine': ['Positive', 'Negative'],
  'aerobic-culture-genital-vaginal': ['Positive', 'Negative'],
  trichomonas: ['Positive', 'Negative'],
  'fecal-immunochemical-test': ['Positive', 'Negative'],
  other: [],
};

// Observation: Labs Other
export const LAB_OTHER_OBSERVATION_CODE_MAP: Record<string, string> = {
  '5---nucleotidase': "5'-Nucleotidase",
  'alpha-fetoprotein-count': 'Alpha-fetoprotein Count',
  'celiac-ttgiga': 'Celiac Disease-tTG IgA',
  'direct-antiglobulin-test--dat': 'Direct Antiglobulin Test (Coombs Test)',
  'ethanol--blood-alcohol-level': 'Ethanol, Blood Alcohol Level',
  'cytomegalovirus--cmv': 'Cytomegalovirus (CMV)',
  'gamma-glutamyl-transferase': 'Gamma-Glutamyl Transferase',
  gastrin: 'Gastrin',
  glucagon: 'Glucagon',
  'glucose-tolerance-test-fasting--ogt': 'Glucose Tolerance Test Fasting (OGT)',
  'glucose--postprandial': 'Glucose, Postprandial',
  'growth-hormone': 'Growth Hormone',
  haptoglobin: 'Haptoglobin',
  'human-chorionic-gonadotropin': 'Human Chorionic Gonadotropin',
  '17-hydroxycorticosteriods': '17-Hydroxycorticosteriods',
  'insulin-assay': 'Insulin Assay',
  prolactin: 'Prolactin',
  'rabies-neutralizing-antibody': 'Rabies Neutralizing Antibody',
  'rheumatoid-factor': 'Rheumatoid Factor',
  'sex-hormone-binding-globulin-shbg': 'Sex hormone binding globulin (SHBG)',
  'sickle-cell': 'Sickle Cell',
  'testosterone-free': 'Testosterone, Free',
  testosterone: 'Testosterone, Total',
  'urate-uric-acid--blood': 'Urate/Uric Acid, Blood',
  'west-nile-virus': 'West Nile Virus',
  'vancomycin-pre': 'Vancomycin Pre',
  'vancomycin-random': 'Vancomycin Random',
  'vitamin-b12': 'Vitamin B12',
  'vitamin-d': 'Vitamin D',
  esr: 'ESR',
  'lactate-dehydrogenase--serum': 'Lactate Dehydrogenase (Serum)',
  'luteinizing-hormone': 'Luteinizing Hormone (LH)',
  'luteinizing-hormone-no-pregnancy-hrt-and-post-menopausal':
    'LH (non-pregnant, post-menopausal, without HRT)',
  progesterone: 'Progesterone',
  'follicle-stimulating-hormone':
    'Follicle-stimulating hormone/ Follitropin (FSH)',
  estradiol: 'Estradiol',
  '25-hydroxy-vitamin-d3': '25 Hydroxy Vitamin D3',
  'dehydroepiandrosterone-sulfate--dheas':
    'Dehydroepiandrosterone sulfate (DHEAS)',
  homocysteine: 'Homocysteine',
  'insulin-like-growth-factor-1--igf-1': 'Insulin-like Growth Factor 1 (IGF-1)',
  'glucose-6-phosphate-dehydrogenase': 'Glucose-6-Phosphate Dehydrogenase',
  'adrenocorticotropic-hormone-plasma-acth':
    'Adrenocorticotropic hormone, plasma (ACTH)',
  'serum-zinc': 'Zinc (serum)',
  'cancer-antigen-125': 'Cancer Antigen 125',
  'fasting-insulin': 'Fasting Insulin',
  other: 'Other',
};

export const LAB_OTHER_UNIT_MAP: Record<string, string[]> = {
  '5---nucleotidase': ['IU/L'],
  'alpha-fetoprotein-count': ['mcg/L', 'ng/mL'],
  'celiac-ttgiga': ['U/mL'],
  'direct-antiglobulin-test--dat': ['Positive', 'Negative'],
  'ethanol--blood-alcohol-level': ['Positive', 'Negative'],
  'cytomegalovirus--cmv': ['Positive', 'Negative'],
  'gamma-glutamyl-transferase': ['IU/L', 'U/L'],
  gastrin: ['ng/L', 'pg/mL'],
  glucagon: ['ng/L', 'pg/mL'],
  'glucose-tolerance-test-fasting--ogt': ['mmol/L', 'mg/dL'],
  'glucose--postprandial': ['mmol/L', 'mg/dL'],
  'growth-hormone': ['mcg/L', 'ng/L'],
  haptoglobin: ['g/L'],
  'human-chorionic-gonadotropin': ['Positive', 'Negative'],
  '17-hydroxycorticosteriods': ['mcmol/24 hrs', 'mg/24 hrs'],
  'insulin-assay': ['pmol/L', 'uU/L'],
  prolactin: ['mcg/L', 'ng/L'],
  'rabies-neutralizing-antibody': [],
  'rheumatoid-factor': ['IU/mL'],
  'sex-hormone-binding-globulin-shbg': ['nmol/L'],
  'sickle-cell': ['Positive', 'Negative'],
  'testosterone-free': ['ng/dL', 'pmol/L'],
  testosterone: ['ng/dL', 'nmol/L'],
  'urate-uric-acid--blood': ['mcmol/L', 'mg/dL'],
  'west-nile-virus': ['Positive', 'Negative'],
  'vancomycin-pre': ['mg/L'],
  'vancomycin-random': ['mg/L'],
  'vitamin-b12': ['pmol/L', 'pg/mL'],
  'vitamin-d': ['nmol/L', 'ng/mL'],
  esr: [],
  'lactate-dehydrogenase--serum': ['mmol/L', 'mg/dL'],
  'luteinizing-hormone': ['IU/L'],
  'luteinizing-hormone-no-pregnancy-hrt-and-post-menopausal': ['IU/L'],
  progesterone: ['nmol/L'],
  'follicle-stimulating-hormone': ['IU/L'],
  estradiol: ['pmol/L'],
  '25-hydroxy-vitamin-d3': ['nmol/L'],
  'dehydroepiandrosterone-sulfate--dheas': ['umol/L'],
  homocysteine: ['umol/L'],
  'insulin-like-growth-factor-1--igf-1': ['ng/mL'],
  'glucose-6-phosphate-dehydrogenase': ['U/g Hb'],
  'adrenocorticotropic-hormone-plasma-acth': ['pmol/L'],
  'serum-zinc': ['umol/L'],
  'cancer-antigen-125': ['U/mL'],
  'fasting-insulin': ['mIU/L'],
  other: [],
};
// Observation: Labs Other Urine Analyses

// Observation: Labs Pathology
export const LAB_PATHOLOGY_OBSERVATION_CODE_MAP: Record<string, string> = {
  'cytogenetics-blood': 'Cytogenetics Blood',
  'cytogenetics-bone-core-biopsy': 'Cytogenetics Bone Core Biopsy',
  'cytogenetics-bone-marrow-aspirate': 'Cytogenetics Bone Marrow Aspirate',
  'fine-needle-aspiration-cytology': 'Fine Needle Aspiration Cytology',
  'pleural-fluid-cytology': 'Pleural Fluid Cytology',
  'peritoneal-fluid-cytology': 'Peritoneal Fluid Cytology',
  'pericardial-fluid-cytology': 'Pericardial Fluid Cytology',
  'pap-test-cytology': 'Pap Test Cytology',
  'molecular-bone-marrow-aspirate': 'Molecular Bone Marrow Aspirate',
  other: 'Other',
};

export const LAB_PATHOLOGY_UNIT_MAP: Record<string, string[]> = {
  'cytogenetics-blood': [],
  'cytogenetics-bone-core-biopsy': [],
  'cytogenetics-bone-marrow-aspirate': [],
  'fine-needle-aspiration-cytology': [],
  'pleural-fluid-cytology': [],
  'peritoneal-fluid-cytology': [],
  'pericardial-fluid-cytology': [],
  'pap-test-cytology': [],
  'molecular-bone-marrow-aspirate': [],
  other: [],
};
// Observation: Labs Thyroid
export const LAB_THYROID_OBSERVATION_CODE_MAP: Record<string, string> = {
  parathyroid: 'Parathyroid',
  thyroglobulin: 'Thyroglobulin',
  'thyroid-stimulating-hormone--tsh': 'Thyroid-Stimulating Hormone (TSH)',
  'thyroxine--free--t4': 'Thyroxine, Free (T4)',
  'thyroxine--total': 'Thyroxine, Total',
  'triiodothyronine--free-t3': 'Triiodothyronine (Free T3)',
  'triiodothyronine--total-t3': 'Total Triiodothyronine (Total T3)',
  'thyroglobulin-antibodies-tgab': 'Thyroglobulin Antibodies (TgAb)',
  'thyroid-peroxidase-antibodies-tpoab':
    'Thyroid Peroxidase Antibodies (TPOAb)',
  'thyroid-receptor-trab': 'Thyroid Receptor (TRAB)',
  other: 'Other',
};

export const LAB_THYROID_UNIT_MAP: Record<string, string[]> = {
  parathyroid: ['ng/L', 'pmol/L'],
  thyroglobulin: ['mcg/L', 'ng/mL'],
  'thyroid-stimulating-hormone--tsh': ['mIU/L', 'uIU/mL'],
  'thyroxine--free--t4': ['pmol/L', 'ng/dL'],
  'thyroxine--total': ['nmol/L', 'mcg/dL'],
  'triiodothyronine--free-t3': ['pmol/L', 'ng/dL'],
  'triiodothyronine--total-t3': ['nmol/L'],
  'thyroglobulin-antibodies-tgab': ['U/mL'],
  'thyroid-peroxidase-antibodies-tpoab': ['U/mL'],
  'thyroid-receptor-trab': ['IU/L'],
  other: [],
};

// Observation: Labs Urine Analyses
export const LAB_URINE_OBSERVATION_CODE_MAP: Record<string, string> = {
  color: 'Color',
  ph: 'pH',
  appearance: 'Appearance',
  'glucose--urine': 'Glucose, Urine',
  ketones: 'Ketones',
  blood: 'Blood',
  crystals: 'Crystals',
  'specific-gravity': 'Specific Gravity',
  'protein--urine': 'Protein, Urine',
  'nitrite--urine': 'Nitrite, Urine',
  'leukocyte--urine': 'Leukocyte, Urine',
  'bilirubin--urine': 'Bilirubin, Urine',
  urobilinogen: 'Urobilinogen, Urine (24-hr)',
  'glucose--urine--24-hr': 'Glucose, Urine (24-hr)',
  'potassium--urine': 'Potassium, Urine',
  'cortisol--urine': 'Cortisol, Urine',
  'myoglobin--urine': 'Myoglobin, Urine',
  'creatinine-urine': 'Creatinine, Urine',
  'nicotine--urine': 'Nicotine, Urine',
  'sodium-urine': 'Sodium, Urine',
  'microalbumin-urine': 'Microalbumin, Urine',
  'microalbumin/creatinine-ratio': 'Microalbumin/Creatinine Ratio (ACR)',
  'estrogen--urine': 'Estrogen, Urine',
  'zinc--urine': 'Zinc, Urine',
  'wbc-urine': 'WBC, Urine (Microscopic)',
  'rbc-urine': 'RBC, Urine (Microscopic)',
  'bacteria-ua': 'Bacteria UA (urine)',
  'mucus-ua': 'Mucus UA (urine)',
  'hyaline-cast': 'Hyaline cast (urine)',
  'urinalysis-microscopic': 'Urinalysis Microscopic',
  'glomerular-filtration-rate': 'GFR (Glomerular Filtration Rate)',
  'wbc-esterase': 'WBC esterase',
  'bun/creatinine-ratio': 'BUN/Creatinine Ratio',
  urea: 'Urea',
  amylase_urine: 'Amylase, Urine',
  urobilinogen_urine_random: 'Urobilinogen, Urine Random',
  'urate--urine': 'Urate, Urine',
  'hemoglobin-urine': 'Hemoglobin, Urine',
  erythrocytes: 'Erythrocytes',
  aldosterone: 'Aldosterone',
  renin: 'Renin',
  'aldosterone-renin-ratio': 'Aldosterone/Renin Ratio',
  osmolality: 'Osmolality - Urine 24 Hour',
  other: 'Other',
};

export const LAB_URINE_UNIT_MAP: Record<string, string[]> = {
  color: [],
  pH: [],
  appearance: [],
  'glucose--urine': ['Positive', 'Negative'],
  ketones: ['Positive', 'Negative'],
  blood: ['Positive', 'Negative'],
  crystals: ['Positive', 'Negative'],
  'specific-gravity': ['No Unit'],
  'protein--urine': ['Positive', 'Negative'],
  'nitrite--urine': ['Positive', 'Negative'],
  'leukocyte--urine': ['Positive', 'Negative'],
  'bilirubin--urine': ['Positive', 'Negative'],
  urobilinogen: ['mg/24 hr', 'Ehrlich units/24 hr'],
  'glucose--urine--24-hr': ['mmol/day', 'g/24 hr'],
  'potassium--urine': ['mmol/day', 'mEq/24 hr'],
  'cortisol--urine': ['mmol/day', 'mEq/24 hr'],
  'myoglobin--urine': ['mg/mL'],
  'creatinine-urine': ['mg/day', 'mmol/L'],
  'nicotine--urine': ['Positive', 'Negative'],
  'sodium-urine': ['mmol/L'],
  'microalbumin-urine': ['mg/L', 'mg/dL'],
  'microalbumin/creatinine-ratio': ['mg/mmol'],
  'estrogen--urine': ['mcg/24 hr', 'nmol/L'],
  'zinc--urine': ['umol/L', 'ug/dL'],
  'wbc-urine': [],
  'rbc-urine': [],
  'bacteria-ua': ['Positive', 'Negative'],
  'mucus-ua': ['Positive', 'Negative'],
  'hyaline-cast': ['Positive', 'Negative'],
  'urinalysis-microscopic': [],
  'glomerular-filtration-rate': ['mL/min/1.73 m2'],
  'wbc-esterase': ['Positive', 'Negative'],
  'bun/creatinine-ratio': [],
  urea: ['mmol/L'],
  amylase_urine: [],
  urobilinogen_urine_random: ['mcmol/L', 'mg/dL'],
  'urate--urine': ['mmol/day', 'mg/24 hrs'],
  'hemoglobin-urine': ['Positive', 'Negative'],
  erythrocytes: ['Positive', 'Negative'],
  aldosterone: ['pmol/L'],
  renin: ['uIU/mL'],
  'aldosterone-renin-ratio': ['pmol/L/uIU/mL'],
  osmolality: ['mmol/Kg'],
  other: [],
};

/**
 * @param code  Lab report's code
 * @returns Correct lab report's unit
 */
export const getLabReportMap = (
  code: string
): {
  types: Record<string, string> | null;
  units: Record<string, string[] | undefined> | null;
} => {
  switch (code) {
    case 'labs--arterial-blood-gases':
      return {
        types: LAB_ARTERIAL_OBSERVATION_CODE_MAP,
        units: LAB_ARTERIAL_UNIT_MAP,
      };
    case 'labs--blood-immunology':
      return {
        types: LAB_IMMUNOLOGY_OBSERVATION_CODE_MAP,
        units: LAB_IMMUNOLOGY_UNIT_MAP,
      };
    case 'labs--cardiac':
      return {
        types: LAB_CARDIAC_OBSERVATION_CODE_MAP,
        units: LAB_CARDIAC_UNIT_MAP,
      };
    case 'labs--cbc':
      return { types: LAB_CBC_OBSERVATION_CODE_MAP, units: LAB_CBC_UNIT_MAP };
    case 'labs--chemistry':
      return {
        types: LAB_CHEMISTRY_OBSERVATION_CODE_MAP,
        units: LAB_CHEMISTRY_UNIT_MAP,
      };
    case 'labs--coagulation':
      return {
        types: LAB_COAGULATION_OBSERVATION_CODE_MAP,
        units: LAB_COAGULATION_UNIT_MAP,
      };
    case 'labs--electrolytes':
      return {
        types: LAB_ELECTROLYTES_OBSERVATION_CODE_MAP,
        units: LAB_ELECTROLYTES_UNIT_MAP,
      };
    case 'labs--genetics':
      return {
        types: LAB_GENETICS_OBSERVATION_CODE_MAP,
        units: LAB_GENETICS_UNIT_MAP,
      };
    case 'labs--lipids':
      return {
        types: LAB_LIPID_OBSERVATION_CODE_MAP,
        units: LAB_LIPID_UNIT_MAP,
      };
    case 'labs--liver-function':
      return {
        types: LAB_LIVER_OBSERVATION_CODE_MAP,
        units: LAB_LIVER_UNIT_MAP,
      };
    case 'labs--microbiology':
      return {
        types: LAB_MICROBIOLOGY_OBSERVATION_CODE_MAP,
        units: LAB_MICROBIOLOGY_UNIT_MAP,
      };
    case 'labs--other':
      return {
        types: LAB_OTHER_OBSERVATION_CODE_MAP,
        units: LAB_OTHER_UNIT_MAP,
      };
    case 'labs--pathology':
      return {
        types: LAB_PATHOLOGY_OBSERVATION_CODE_MAP,
        units: LAB_PATHOLOGY_UNIT_MAP,
      };
    case 'labs--thyroid':
      return {
        types: LAB_THYROID_OBSERVATION_CODE_MAP,
        units: LAB_THYROID_UNIT_MAP,
      };
    case 'labs--urine-analyses':
      return {
        types: LAB_URINE_OBSERVATION_CODE_MAP,
        units: LAB_URINE_UNIT_MAP,
      };
    case 'labs--other-urine-analyses':
    default:
      return { types: null, units: null };
  }
};

export const COMPLETE_LAB_OBSERVATION_CODE_MAP = {
  ...LAB_ARTERIAL_OBSERVATION_CODE_MAP,
  ...LAB_IMMUNOLOGY_OBSERVATION_CODE_MAP,
  ...LAB_CARDIAC_OBSERVATION_CODE_MAP,
  ...LAB_CBC_OBSERVATION_CODE_MAP,
  ...LAB_CHEMISTRY_OBSERVATION_CODE_MAP,
  ...LAB_COAGULATION_OBSERVATION_CODE_MAP,
  ...LAB_ELECTROLYTES_OBSERVATION_CODE_MAP,
  ...LAB_GENETICS_OBSERVATION_CODE_MAP,
  ...LAB_LIPID_OBSERVATION_CODE_MAP,
  ...LAB_LIVER_OBSERVATION_CODE_MAP,
  ...LAB_MICROBIOLOGY_OBSERVATION_CODE_MAP,
  ...LAB_OTHER_OBSERVATION_CODE_MAP,
  ...LAB_PATHOLOGY_OBSERVATION_CODE_MAP,
  ...LAB_THYROID_OBSERVATION_CODE_MAP,
  ...LAB_URINE_OBSERVATION_CODE_MAP,
};
